import { useEffect, useMemo, useState } from "react";
import { Consultation } from "../../../Common/interfaces/doctor.interfaces";
import { fetchConsultationHistory } from "../https/history_module.https";
import { FilterType, HistoryGroup, HistoryModuleHookReturn } from "../interfaces/history_module.interface";

const getDateLabel = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === now.toDateString())
    return `Today — ${date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`;
  if (date.toDateString() === yesterday.toDateString())
    return `Yesterday — ${date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`;
  return date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
};

const groupByDate = (items: Consultation[]): HistoryGroup[] => {
  const map: Record<string, Consultation[]> = {};
  const order: string[] = [];
  items.forEach((item) => {
    const key = new Date(item.requested_at).toDateString();
    if (!map[key]) { map[key] = []; order.push(key); }
    map[key].push(item);
  });
  return order.map((key) => ({ label: getDateLabel(map[key][0].requested_at), items: map[key] }));
};

export const useHistoryModule = (): HistoryModuleHookReturn => {
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilterState] = useState<FilterType>("all");
  const [completedCount, setCompletedCount] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    const params: any = { page, limit };
    const now = new Date();
    if (activeFilter === "week") {
      const weekAgo = new Date(); weekAgo.setDate(now.getDate() - 7);
      params.dateFrom = weekAgo.toISOString();
      params.dateTo = now.toISOString();
    } else if (activeFilter === "month") {
      const monthAgo = new Date(); monthAgo.setMonth(now.getMonth() - 1);
      params.dateFrom = monthAgo.toISOString();
      params.dateTo = now.toISOString();
    }
    fetchConsultationHistory(params)
      .then((r) => {
        setConsultations(r.data?.data || []);
        setTotal(r.data?.total || 0);
        setCompletedCount(r.data?.completedCount || 0);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeFilter]);

  const setActiveFilter = (f: FilterType) => {
    setActiveFilterState(f);
    setPage(1);
  };

  const filtered = useMemo(() => {
    let list = consultations;
    if (activeFilter === "completed") list = list.filter((c) => c.status === "completed");
    if (activeFilter === "cancelled") list = list.filter((c) => c.status === "cancelled");
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          (c.patient_name || "").toLowerCase().includes(q) ||
          String(c.ticket_id || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [consultations, activeFilter, search]);


  const withDuration = consultations.filter((c) => c.call_duration_seconds);
  const avgDuration = withDuration.length
    ? Math.round(
        withDuration.reduce((a, c) => a + (c.call_duration_seconds || 0), 0) /
          withDuration.length /
          60
      )
    : 0;

  const groups = groupByDate(filtered);

  return {
    isLoading,
    filtered,
    groups,
    total,
    page,
    limit,
    search,
    activeFilter,
    completedCount,
    avgDuration,
    setSearch,
    setActiveFilter,
    setPage,
  };
};
